import { isNumeric } from '../../util/stringUtil';
import { EDU } from '../../constants/general';

/**
 * Validates leadershipPositions array based on version.
 * @param {Array} leadershipPositions - Array of leadership position objects
 * @param {number} version - Scholarship version (2 for new fields)
 * @returns {{valid: boolean, requiredFields: Array}} - Validation result and missing field requirements
 */
export function validateLeadershipPositions(leadershipPositions, version) {
  let valid = true;
  let requiredFields = [];
  if (leadershipPositions && leadershipPositions.length > 0) {
    for (let i = 0; i < leadershipPositions.length; i++) {
      const act = leadershipPositions[i];
      if (version === 2) {
        // For version 2, require schoolYear, duration, and positionOrDescription
        if (
          !act.schoolYear ||
          act.schoolYear.length === 0 ||
          !act.duration ||
          act.duration.length === 0 ||
          !act.positionOrDescription ||
          act.positionOrDescription.length === 0
        ) {
          requiredFields.push({
            requirement: `Ensure each Leadership Position has the school year, duration, and position/description entered (Row #${i + 1}).`,
          });
          valid = false;
        }
      } else {
        // For other versions, require schoolYear, position, and description
        if (
          !act.schoolYear ||
          act.schoolYear.length === 0 ||
          !act.position ||
          act.position.length === 0 ||
          !act.description ||
          act.description.length === 0
        ) {
          requiredFields.push({
            requirement: 'Ensure each Leadership Position has the school year, position, and description entered.',
          });
          valid = false;
        }
      }
    }
  }
  return { valid, requiredFields };
}

/**
 * Validates post-graduate contact info fields for scholarship applications (version != 2).
 * @param {Object} form - The scholarship application form object
 * @returns {{valid: boolean, requiredFields: Array}} - Validation result and missing field requirements
 */
export function validatePostGraduateContactInfo(form) {
  let valid = true;
  let requiredFields = [];

  if (!form.address1 || form.address1.length === 0) {
    requiredFields.push({ requirement: 'Please enter a Street Address.' });
    valid = false;
  }
  if (!form.city || form.city.length === 0) {
    requiredFields.push({ requirement: 'Please enter a City.' });
    valid = false;
  }
  if (!form.state || form.state.length === 0) {
    requiredFields.push({ requirement: 'Please select a State.' });
    valid = false;
  }
  if (!form.phoneNumber || form.phoneNumber.length < 10) {
    requiredFields.push({ requirement: 'Please enter a 10 digit phone number.' });
    valid = false;
  }
  if (!form.zipCode || form.zipCode.length < 5) {
    requiredFields.push({ requirement: 'Please enter a 5 digit zip code.' });
    valid = false;
  }

  return { valid, requiredFields };
}

/**
 * Validates required education fields in the scholarship application form.
 * @param {Object} form - The scholarship application form object
 * @returns {{valid: boolean, requiredFields: Array}} - Validation result and missing field requirements
 */
export function validateEduFields(form) {
  let valid = true;
  let requiredFields = [];
  // const suffix = form.postGradPlan === '4-Year College' ? '4Year' : form.postGradPlan === '2-Year College' ? '2Year' : 'CareerTraining';
  // Helper to get field name for the plan
  // const field = (base) => `${base}${suffix}`;
  const field = (base) => `${base}`;
  const actScore = form[field('actScore')];
  if (!isNumeric(actScore) || actScore > 36) {
    requiredFields.push({ requirement: 'ACT Score must be a number between 0 and 36.' });
    valid = false;
  }
  const actDate = form[field('actDate')];
  if (!actDate || actDate.isValid === false) {
    requiredFields.push({ requirement: 'Please enter ACT Date.' });
    valid = false;
  }
  const fafsaCompleted = form[field('fafsaCompleted')];
  if (!fafsaCompleted || fafsaCompleted.length === 0) {
    requiredFields = [...requiredFields, { requirement: 'Please indicate FAFSA completion.' }];
    valid = false;
  }
  const studentAidIndex = form[field('studentAidIndex')];
  if (!studentAidIndex || studentAidIndex.length === 0) {
    requiredFields = [...requiredFields, { requirement: 'Please indicate Student Aid Index.' }];
    valid = false;
  }
  const certifications = form[field('certifications')];
  if (!certifications || certifications.length === 0) {
    requiredFields = [...requiredFields, { requirement: 'Please indicate List any certifications you have acquired.' }];
    valid = false;
  }
  const schoolName = form[field('schoolName')];
  if (!schoolName || schoolName.length === 0) {
    requiredFields = [...requiredFields, { requirement: 'Please enter Name of the school you plan to attend.' }];
    valid = false;
  }
  const intendedMajor = form[field('intendedMajor')];
  if (!intendedMajor || intendedMajor.length === 0) {
    requiredFields = [...requiredFields, { requirement: 'Please enter Intended Major.' }];
    valid = false;
  }
  const housingPlan = form[field('housingPlan')];
  if (!housingPlan || housingPlan.length === 0) {
    requiredFields = [...requiredFields, { requirement: 'Please select a Housing Plan.' }];
    valid = false;
  }
  const outstandingNeeds = form[field('outstandingNeeds')];
  if (!outstandingNeeds || outstandingNeeds.length === 0) {
    requiredFields = [...requiredFields, { requirement: 'Please enter Outstanding Needs' }];
    valid = false;
  }

  return { valid, requiredFields };
}

/**
 * Generic field validator for required fields.
 * @param {Object} form - The form object
 * @param {Array} requiredFields - Array of field names to validate
 * @returns {{valid: boolean, requiredFields: Array}}
 */
function validateFields(form, requiredFields) {
  let valid = true;
  let required = [];
  for (const field of requiredFields) {
    if (!form[field] || (typeof form[field] === 'string' && form[field].length === 0)) {
      required.push({ requirement: `Please enter ${field}.` });
      valid = false;
    }
  }
  return { valid, requiredFields: required };
}

/**
 * Validates required military fields in the scholarship application form.
 * @param {Object} form - The scholarship application form object
 * @returns {{valid: boolean, requiredFields: Array}}
 */
export function validateMilitaryFields(form) {
  const requiredFields = [
    'militaryBranch',
    'asvabScore',
    'militaryDate',
    'militaryLocation',
    'militaryNextOption',
    'militaryBasicTrainingDate',
    'militaryBasicTrainingLocation',
  ];
  return validateFields(form, requiredFields);
}

/**
 * Validates required workforce fields in the scholarship application form.
 * @param {Object} form - The scholarship application form object
 * @returns {{valid: boolean, requiredFields: Array}}
 */
export function validateWorkforceFields(form) {
  let requiredFields = ['workForceCurrentlyEmployed'];
  if (form.workForceCurrentlyEmployed === 'no') {
    requiredFields.push('workForcePotentialEmployers');
  } else if (form.workForceCurrentlyEmployed === 'yes') {
    requiredFields = requiredFields.concat([
      'workForceEmployerName',
      'workForceStartDate',
      'workForcePosition',
      'workForcePositionOrCareer',
      'workForceNextStep',
    ]);
    if (form.workForcePositionOrCareer === 'other') {
      requiredFields.push('workForcePositionOther');
    }
  }
  return validateFields(form, requiredFields);
}

/**
 * Validates all version 2 post-graduate fields based on postGradPlan.
 * @param {Object} form - The scholarship application form object
 * @returns {{valid: boolean, requiredFields: Array}}
 */
export function validateVersion2PostGraduateFields(form) {
  let rc = true;
  let requiredFields = [];
  let result = { valid: true, requiredFields: [] };

  if (EDU.includes(form.postGradPlan)) {
    result = validateEduFields(form);
  } else if (form.postGradPlan === 'Military') {
    result = validateMilitaryFields(form);
  } else if (form.postGradPlan === 'Workforce') {
    result = validateWorkforceFields(form);
  }

  if (!result.valid) {
    requiredFields = [...requiredFields, ...result.requiredFields];
    rc = false;
  }

  return { valid: rc, requiredFields };
}

/**
 * Validates common post-graduate fields required for all plans.
 * @param {Object} form - The scholarship application form object
 * @returns {{valid: boolean, requiredFields: Array}}
 */
export function validatePostGraduateCommonFields(form) {
  let valid = true;
  let requiredFields = [];

  if (!form.finalPostGradPlan || form.finalPostGradPlan.length === 0) {
    requiredFields.push({
      requirement: 'Please explain, in detail, my final post-graduation plan. Include details of what, when, where and how I am paying for my plan.',
    });
    valid = false;
  }
  if (!form.outstandingFinalPlans || form.outstandingFinalPlans.length === 0) {
    requiredFields.push({ requirement: "Please answer 'What needs to be done to finalize my plan'." });
    valid = false;
  }
  if (!form.finalPlanChallenges || form.finalPlanChallenges.length === 0) {
    requiredFields.push({ requirement: "Please enter the 'Challenges/Barriers to my plan'." });
    valid = false;
  }

  return { valid, requiredFields };
}

/**
 * Validates scholarship needs and essay fields.
 * @param {Object} form - The scholarship application form object.
 * @returns {Object} { valid: boolean, requiredFields: array }
 */
export function validateScholarshipFields(form) {
  let rc = true;
  let requiredFields = [];
  let needsSelected = 0;

  if (form.hasOwnProperty('HousingSupportNeedsChecked') && form.HousingSupportNeedsChecked === true) {
    needsSelected += 1;
    if (!form.HousingSupportNeedsDesc || form.HousingSupportNeedsDesc.length === 0) {
      requiredFields.push({ requirement: 'Please enter a description for Housing Support Needs.' });
      rc = false;
    }
  }
  if (form.hasOwnProperty('MedicalInsuranceNeedsChecked') && form.MedicalInsuranceNeedsChecked) {
    needsSelected += 1;
    if (!form.MedicalInsuranceNeedsDesc || form.MedicalInsuranceNeedsDesc.length === 0) {
      requiredFields.push({ requirement: 'Please enter a description for Medical Insurance Needs.' });
      rc = false;
    }
  }
  if (form.hasOwnProperty('HealthNeedsChecked') && form.HealthNeedsChecked) {
    needsSelected += 1;
    if (!form.HealthNeedsDesc || form.HealthNeedsDesc.length === 0) {
      requiredFields.push({ requirement: 'Please enter a description for Health Needs.' });
      rc = false;
    }
  }
  if (form.hasOwnProperty('EmploymentNeedsChecked') && form.EmploymentNeedsChecked) {
    needsSelected += 1;
    if (!form.EmploymentNeedsDesc || form.EmploymentNeedsDesc.length === 0) {
      requiredFields.push({ requirement: 'Please enter a description for Employment Needs.' });
      rc = false;
    }
  }
  if (form.hasOwnProperty('TuitionNeedsChecked') && form.TuitionNeedsChecked) {
    needsSelected += 1;
    if (!form.TuitionNeedsDesc || form.TuitionNeedsDesc.length === 0) {
      requiredFields.push({ requirement: 'Please enter a description for Tuition Needs.' });
      rc = false;
    }
  }
  if (form.hasOwnProperty('WorkforceSuppliesNeedsChecked') && form.WorkforceSuppliesNeedsChecked) {
    needsSelected += 1;
    if (!form.WorkforceSuppliesNeedsDesc || form.WorkforceSuppliesNeedsDesc.length === 0) {
      requiredFields.push({ requirement: 'Please enter a description for Workforce Supplies Needs.' });
      rc = false;
    }
  }
  if (form.hasOwnProperty('TransportationNeedsChecked') && form.TransportationNeedsChecked) {
    needsSelected += 1;
    if (!form.TransportationNeedsDesc || form.TransportationNeedsDesc.length === 0) {
      requiredFields.push({ requirement: 'Please enter a description for Transportation Needs.' });
      rc = false;
    }
  }
  if (form.hasOwnProperty('OtherNeedsChecked') && form.OtherNeedsChecked) {
    needsSelected += 1;
    if (!form.OtherNeedsDesc || form.OtherNeedsDesc.length === 0) {
      requiredFields.push({ requirement: 'Please enter a description for Other Needs.' });
      rc = false;
    }
  }
  if (needsSelected === 0) {
    requiredFields.push({ requirement: "Please enter one or more 'Outstanding Scholarship/Post-Graduation Support Needs'." });
    rc = false;
  }

  return { valid: rc, requiredFields };
}

/**
 * Validates extracurricular activities for both v1 and v2 forms.
 * @param {Object} form - The scholarship application form object
 * @param {number} version - Scholarship version (2 for new fields)
 * @returns {{valid: boolean, requiredFields: Array}} - Validation result and missing field requirements
 */
export function validateExtraCurricularActivities(form, version) {
  let valid = true;
  let requiredFields = [];
  if (version === 2) {
    const list = Array.isArray(form?.extraCurricularActivitiesV2) ? form.extraCurricularActivitiesV2 : [];
    for (let i = 0; i < list.length; i++) {
      const act = list[i];
      if (!act.schoolYear || act.schoolYear.length === 0) {
        requiredFields.push({ requirement: `Please enter School Year for Extra-Curricular Activity (Row #${i + 1}).` });
        valid = false;
      }
      if (!act.completedSeasonOrYear || act.completedSeasonOrYear.length === 0) {
        requiredFields.push({ requirement: `Please enter Completed Season/Year for Extra-Curricular Activity (Row #${i + 1}).` });
        valid = false;
      }
      if (!act.activityAndDescription || act.activityAndDescription.length === 0) {
        requiredFields.push({ requirement: `Please enter Activity & Description for Extra-Curricular Activity (Row #${i + 1}).` });
        valid = false;
      }
    }
  } else {
    const list = Array.isArray(form?.extraCurricularActivites) ? form.extraCurricularActivites : [];
    for (let i = 0; i < list.length; i++) {
      const act = list[i];
      if (act.schoolYear && act.schoolYear.length > 0 && (!act.description || act.description.length === 0)) {
        requiredFields.push({ requirement: `Ensure each Extra-Curricular Activity has both the Year and activity entered (Row #${i + 1}).` });
        valid = false;
      }
      if (act.description && act.description.length > 0 && (!act.schoolYear || act.schoolYear.length === 0)) {
        requiredFields.push({ requirement: `Ensure each Extra-Curricular Activity has both the year and Activity entered (Row #${i + 1}).` });
        valid = false;
      }
    }
  }
  return { valid, requiredFields };
}
